import { mainApi } from '../index';
import type {
    Automation,
    AutomationFormData,
    FetchAutomationAnalyticsResponse,
    FetchAutomationsResponse,
    FetchInstagramBusinessAccountsResponse,
    FetchInstagramPostsResponse,
    InstagramPost,
    WeeklyPostsResponse,
} from './types';

/**
 * Fetch all automations for the current user
 * @returns Promise resolving to array of automations
 */
export async function fetchAutomations(): Promise<Automation[]> {
    const { data } = await mainApi.get<FetchAutomationsResponse>('v1/automations');
    return data.automations || [];
}

/**
 * Fetch a single automation by ID
 * @param automationId - The automation ID to fetch
 * @returns Promise resolving to automation data
 */
export async function fetchAutomation(automationId: number): Promise<Automation> {
    const { data } = await mainApi.get<Automation>(`v1/automations/${automationId}`);
    return data;
}

/**
 * Fetch automation analytics
 * @param automationIds - Array of automation IDs to fetch analytics for
 * @returns Promise resolving to analytics data
 */
export async function fetchAutomationAnalytics(automationIds: number[]): Promise<FetchAutomationAnalyticsResponse> {
    const { data } = await mainApi.get<FetchAutomationAnalyticsResponse>('v1/automations/analytics', {
        params: {
            automation_ids: automationIds.join(','),
        },
    });
    return data;
}

/**
 * Fetch Instagram posts for post picker
 * @returns Promise resolving to array of Instagram posts
 */
export async function fetchInstagramPosts(): Promise<FetchInstagramPostsResponse> {
    const { data } = await mainApi.get<InstagramPost[]>('v1/automations/instagram-posts-metadata');
    // API returns array directly, not wrapped in { data: [] }
    return { data };
}

/**
 * Fetch Instagram business accounts
 * @returns Promise resolving to array of Instagram business accounts
 */
export async function fetchInstagramBusinessAccounts(): Promise<FetchInstagramBusinessAccountsResponse> {
    const { data } = await mainApi.get<FetchInstagramBusinessAccountsResponse>(
        'v1/automations/instagram_business_accounts'
    );
    return data;
}

/**
 * Fetch weekly Instagram posts count
 * @returns Promise resolving to post count
 */
export async function fetchWeeklyInstagramPosts(): Promise<WeeklyPostsResponse> {
    const { data } = await mainApi.get<WeeklyPostsResponse>('v1/automations/weekly-instagram-posts');
    return data;
}

/**
 * Create a new automation
 * @param automationData - The automation data to create
 * @returns Promise resolving to created automation
 */
export async function createAutomation(automationData: AutomationFormData): Promise<Automation> {
    const { data } = await mainApi.post<Automation>('v1/automations', automationData);
    return data;
}

/**
 * Update an existing automation
 * @param automationId - Automation ID
 * @param automationData - Updated automation data
 * @returns Promise resolving to updated automation
 */
export async function updateAutomation(
    automationId: number,
    automationData: Partial<AutomationFormData>
): Promise<Automation> {
    const { data } = await mainApi.put<Automation>(`v1/automations/${automationId}`, automationData);
    return data;
}

/**
 * Delete an automation (soft delete - sets status to 'deleted')
 * @param automationId - Automation ID to delete
 * @returns Promise resolving to deleted automation
 */
export async function deleteAutomation(automationId: number): Promise<Automation> {
    const { data } = await mainApi.delete<Automation>(`v1/automations/${automationId}`);
    return data;
}

/**
 * Publish an automation (change status to published)
 * @param automationId - Automation ID to publish
 * @param automation - Full automation object
 * @returns Promise resolving to updated automation
 */
export async function publishAutomation(automationId: number, automation: Automation): Promise<Automation> {
    const { data } = await mainApi.put<Automation>(`v1/automations/${automationId}`, {
        ...automation,
        status: 'published',
    });
    return data;
}

/**
 * Unpublish an automation (change status to draft)
 * @param automationId - Automation ID to unpublish
 * @param automation - Full automation object
 * @returns Promise resolving to updated automation
 */
export async function unpublishAutomation(automationId: number, automation: Automation): Promise<Automation> {
    const { data } = await mainApi.put<Automation>(`v1/automations/${automationId}`, {
        ...automation,
        status: 'draft',
    });
    return data;
}
